import { CamelCatalogService, FileTypes, FileTypesResponse } from '../../models';
import { CamelCatalogIndex, ComponentsCatalog } from '../../models/camel/camel-catalog-index';
import { CatalogKind } from '../../models/catalog-kind';
import { CatalogSchemaLoader } from '../../utils/catalog-schema-loader';
import { DynamicCatalog } from '../dynamic-catalog';
import { DynamicCatalogRegistry } from '../dynamic-catalog-registry';
import {
  CamelComponentsProvider,
  CamelDataformatProvider,
  CamelFunctionProvider,
  CamelLanguageProvider,
  CamelLoadbalancerProvider,
  CamelProcessorsProvider,
} from '../providers/camel-components.provider';
import { CamelKameletsProvider } from '../providers/camel-kamelets.provider';

export async function fetchCamelCatalog(options: {
  catalogIndex: CamelCatalogIndex;
  relativeBasePath: string;
  getResourcesContentByType?: (filetype: FileTypes) => Promise<FileTypesResponse[]>;
}): Promise<void> {
  const { catalogIndex, relativeBasePath, getResourcesContentByType } = options;

  /** Camel Component list */
  const camelComponentsFiles = CatalogSchemaLoader.fetchFile<ComponentsCatalog[CatalogKind.Component]>(
    `${relativeBasePath}/${catalogIndex.catalogs.components.file}`,
  );
  /** Full list of Camel Models, used as lookup for processors definitions */
  const camelModelsFiles = CatalogSchemaLoader.fetchFile<ComponentsCatalog[CatalogKind.Processor]>(
    `${relativeBasePath}/${catalogIndex.catalogs.models.file}`,
  );
  /** Short list of patterns (EIPs) to fill the Catalog, as opposed of the CatalogKind.Processor which have all definitions */
  const camelPatternsFiles = CatalogSchemaLoader.fetchFile<ComponentsCatalog[CatalogKind.Pattern]>(
    `${relativeBasePath}/${catalogIndex.catalogs.patterns.file}`,
  );
  /** Short list of entities to fill the Catalog, as opposed of the CatalogKind.Processor which have all definitions */
  const camelEntitiesFiles = CatalogSchemaLoader.fetchFile<ComponentsCatalog[CatalogKind.Entity]>(
    `${relativeBasePath}/${catalogIndex.catalogs.entities.file}`,
  );
  /** Camel Languages list */
  const camelLanguagesFiles = CatalogSchemaLoader.fetchFile<ComponentsCatalog[CatalogKind.Language]>(
    `${relativeBasePath}/${catalogIndex.catalogs.languages.file}`,
  );
  /** Camel Dataformats list */
  const camelDataformatsFiles = CatalogSchemaLoader.fetchFile<ComponentsCatalog[CatalogKind.Dataformat]>(
    `${relativeBasePath}/${catalogIndex.catalogs.dataformats.file}`,
  );
  /** Camel Loadbalancers list */
  const camelLoadbalancersFiles = CatalogSchemaLoader.fetchFile<ComponentsCatalog[CatalogKind.Loadbalancer]>(
    `${relativeBasePath}/${catalogIndex.catalogs.loadbalancers.file}`,
  );
  /** Camel Kamelets definitions list (CRDs) */
  const kameletsFiles = CatalogSchemaLoader.fetchFile<ComponentsCatalog[CatalogKind.Kamelet]>(
    `${relativeBasePath}/${catalogIndex.catalogs.kamelets.file}`,
  );
  /** Camel Kamelets boundaries definitions list (CRDs) */
  const kameletBoundariesFiles = CatalogSchemaLoader.fetchFile<ComponentsCatalog[CatalogKind.Kamelet]>(
    `${relativeBasePath}/${catalogIndex.catalogs.kameletBoundaries.file}`,
  );
  /** Functions catalog */
  const functionsFiles = CatalogSchemaLoader.fetchFile<ComponentsCatalog[CatalogKind.Function]>(
    `${relativeBasePath}/${catalogIndex.catalogs.functions.file}`,
  );

  const [
    camelComponents,
    camelModels,
    camelPatterns,
    camelEntities,
    camelLanguages,
    camelDataformats,
    camelLoadbalancers,
    kamelets,
    kameletBoundaries,
    functions,
  ] = await Promise.all([
    camelComponentsFiles,
    camelModelsFiles,
    camelPatternsFiles,
    camelEntitiesFiles,
    camelLanguagesFiles,
    camelDataformatsFiles,
    camelLoadbalancersFiles,
    kameletsFiles,
    kameletBoundariesFiles,
    functionsFiles,
  ]);

  /**
   * Temporary while we switch all sync API to ASYNC from the DynamicCatalogRegistry.
   * This will be removed once all consumers are refactored to use DynamicCatalogRegistry
   */
  CamelCatalogService.setCatalogKey(CatalogKind.Component, camelComponents.body);
  CamelCatalogService.setCatalogKey(CatalogKind.Processor, camelModels.body);
  CamelCatalogService.setCatalogKey(CatalogKind.Pattern, camelPatterns.body);
  CamelCatalogService.setCatalogKey(CatalogKind.Entity, camelEntities.body);
  CamelCatalogService.setCatalogKey(CatalogKind.Language, camelLanguages.body);
  CamelCatalogService.setCatalogKey(CatalogKind.Dataformat, camelDataformats.body);
  CamelCatalogService.setCatalogKey(CatalogKind.Loadbalancer, camelLoadbalancers.body);
  CamelCatalogService.setCatalogKey(CatalogKind.Kamelet, { ...kameletBoundaries.body, ...kamelets.body });
  CamelCatalogService.setCatalogKey(CatalogKind.Function, functions.body);

  DynamicCatalogRegistry.get().setCatalog(
    CatalogKind.Component,
    new DynamicCatalog(new CamelComponentsProvider(camelComponents.body)),
  );
  DynamicCatalogRegistry.get().setCatalog(
    CatalogKind.Processor,
    new DynamicCatalog(new CamelProcessorsProvider(camelModels.body)),
  );
  DynamicCatalogRegistry.get().setCatalog(
    CatalogKind.Pattern,
    new DynamicCatalog(new CamelProcessorsProvider(camelPatterns.body)),
  );
  DynamicCatalogRegistry.get().setCatalog(
    CatalogKind.Entity,
    new DynamicCatalog(new CamelProcessorsProvider(camelEntities.body)),
  );
  DynamicCatalogRegistry.get().setCatalog(
    CatalogKind.Language,
    new DynamicCatalog(new CamelLanguageProvider(camelLanguages.body)),
  );
  DynamicCatalogRegistry.get().setCatalog(
    CatalogKind.Dataformat,
    new DynamicCatalog(new CamelDataformatProvider(camelDataformats.body)),
  );
  DynamicCatalogRegistry.get().setCatalog(
    CatalogKind.Loadbalancer,
    new DynamicCatalog(new CamelLoadbalancerProvider(camelLoadbalancers.body)),
  );
  DynamicCatalogRegistry.get().setCatalog(
    CatalogKind.Kamelet,
    new DynamicCatalog(
      new CamelKameletsProvider({ ...kameletBoundaries.body, ...kamelets.body }, getResourcesContentByType),
    ),
  );
  DynamicCatalogRegistry.get().setCatalog(
    CatalogKind.Function,
    new DynamicCatalog(new CamelFunctionProvider(functions.body)),
  );
}
